/**
 * PATH: apps/qa/src/App.tsx
 * PURPOSE: Main application entry point for QA Hub.
 *
 * WHY: Provides a unified QA shell with consistent navigation, settings,
 *      and SSO behavior across all hubs.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌────────────────┐
 *   │ SSO/Session  │───▶│ Layout Shell │───▶│ QA Routes       │
 *   └──────────────┘    └──────────────┘    └────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui: DashboardLayout, buildSettingsTabs
 *   - @quotech/auth: useAuth, ProtectedRoute, SessionManager
 *   - QA navigation config + pages
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { 
  DashboardLayout, 
  TabBar,
  useTabNavigation,
  useTabKeyboardShortcuts,
  type User,
  buildSettingsTabs,
  getHubBreadcrumbs,
  useGlobalSearchBridge,
  ErrorBoundary,
  VersionWatcher,
} from '@quotech/ui'
import { useApiClient } from '@quotech/api'
import { useQaAppCapabilities } from '@/lib/useQaAppCapabilities'
import { 
  useAuth, 
  SSOHandler, 
  ProtectedRoute,
  HubGuard,
  startSessionMonitoring,
  stopSessionMonitoring,
  syncIntegrationStatus,
  UnifiedSSOPage,
  useAuthStore,
  getRefreshCookie,
  useComputedIsAdmin,
  useRBAC,
} from '@quotech/auth'
import {
  MODULE_CONFIG,
  NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  EXTERNAL_LINKS,
  COMMANDS,
  HUB_LINKS,
  QUICK_ADD_ITEMS,
  sidebarSectionLabels,
} from './config/navigation'
import { QA_TAB_CONFIG, useQaTabStore } from './config/tabs'
import { OfflineStatusCompact } from './components/OfflineStatus'

// Pages
import {
  DashboardPage,
  RecordsPage,
  RecordDetailPage,
  JobQaJobPage,
  NewInspectionPage,
  TemplatesPage,
  TemplateDetailPage,
  NCRsPage,
  NCRDetailPage,
  ReportsPage,
  ReportEditorPage,
  EvidencePage,
  EvidenceUploadPage,
  SignoffsPage,
  AreaExecutionPage,
  AIInsightsPage,
  AuditLogPage,
  ChatPage,
  PortalPage,
} from './pages'

/**
 * Main layout wrapper using @quotech/ui
 */
function QALayout() {
  const { user, logout, organisationId, organisationName } = useAuth()
  const location = useLocation()
  const onRecordDetail = /^\/records\/(?!new$)[^/]+$/.test(location.pathname)
  const outletClassName = onRecordDetail
    ? 'w-full px-4 sm:px-6 lg:px-8 2xl:px-10 max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-col max-md:overflow-hidden max-md:py-1 md:py-4 md:py-6'
    : 'w-full px-4 sm:px-6 lg:px-8 2xl:px-10 py-4 md:py-6'
  const [settingsOpen, setSettingsOpen] = useState(false)
  const apiClient = useApiClient()

  const handleLogout = useCallback(async () => {
    try {
      const { clearQaOfflineQueues } = await import('@/lib/clearOfflineQueues')
      await clearQaOfflineQueues()
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[QA Hub] Failed to clear offline queues on logout:', err)
    }
    await logout()
  }, [logout])

  // Build breadcrumbs from path
  const breadcrumbs = getHubBreadcrumbs('qa', location.pathname)
  
  // Global Search Bridge - provides API client with auth for search to work
  // CRITICAL: Without this, GlobalSearch UI appears but can't actually search
  const tokenProvider = useMemo(() => ({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getOrgId: () => useAuthStore.getState().organisationId || organisationId,
  }), [organisationId])
  
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  })

  const { canCreateQaJob } = useQaAppCapabilities()
  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null)
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC()

  // Map auth user to UI user format - includes ALL admin-related fields
  // IMPORTANT: organisationName/orgId can be present via shared cookies even if the user profile
  // hasn't been hydrated yet. The Header relies on `user.organisation` to display the label
  // (otherwise it shows the fallback "Organisation").
  const uiUser: User & { displayEmail?: string } | null = useMemo(() => {
    const resolvedOrganisationId = organisationId || user?.organisation_id || undefined
    const resolvedOrganisationName = organisationName || user?.organisation_name || undefined

    // If we truly have no auth context yet, don't pass a user object.
    if (!user && !resolvedOrganisationId && !resolvedOrganisationName) return null

    return {
      id: user?.id || 'current',
      name: user?.name || user?.full_name || user?.email || 'User',
      email: user?.email || '',
      // Header-only authority display; login identity stays on `email`.
      displayEmail: user?.display_email || user?.authority_email || user?.email,
      organisation: resolvedOrganisationName,
      organisationId: resolvedOrganisationId,
      role: user?.role,
      isAdmin: computedIsAdmin,
      // Pass raw Django flags for cached session support
      admin: (user as Record<string, unknown>)?.admin as boolean | undefined,
      is_admin: (user as Record<string, unknown>)?.is_admin as boolean | undefined,
      is_staff: (user as Record<string, unknown>)?.is_staff as boolean | undefined,
      is_superuser: (user as Record<string, unknown>)?.is_superuser as boolean | undefined,
    }
  }, [user, organisationId, organisationName, computedIsAdmin])

  // Build settings tabs using universal helper with QA-specific shortcuts
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      id: user.id,
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name || organisationName,
      role: user.role,
      isAdmin: computedIsAdmin,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'qa',
    hubName: 'QA Hub',
    apiClient,
    organisationId: organisationId || user?.organisation_id,
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name || organisationName,
    },
    organisationData: {
      id: organisationId || user?.organisation_id || '',
      name: organisationName || user?.organisation_name || 'Organisation',
    },
    integrations: [], // QA Hub doesn't have direct integrations, managed at org level
    shortcuts: [
      ...(canCreateQaJob ? [{ label: 'New Inspection', keys: ['⌘', '⇧', 'I'], category: 'QA Hub' }] : []),
      { label: 'New NCR', keys: ['⌘', '⇧', 'N'], category: 'QA Hub' },
    ],
  }), [user, organisationName, organisationId, apiClient, computedIsAdmin, rbacPermissions, hierarchyLevel, canCreateQaJob])

  const tabNav = useTabNavigation(QA_TAB_CONFIG)
  useTabKeyboardShortcuts({ useStore: useQaTabStore, ...tabNav })
  
  // Update SECONDARY_NAV_ITEMS to use action for settings
  const secondaryNavWithSettingsAction = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, href: undefined, action: () => setSettingsOpen(true) }
        : item
    ),
    []
  )

  return (
    <DashboardLayout
      module={MODULE_CONFIG}
      currentHub="qa"
      accessibleModules={accessibleModules}
      navItems={NAV_ITEMS}
      secondaryNavItems={secondaryNavWithSettingsAction}
      externalLinks={EXTERNAL_LINKS}
      user={uiUser}
      onLogout={handleLogout}
      breadcrumbs={breadcrumbs}
      showQuickAdd
      quickAddItems={canCreateQaJob ? QUICK_ADD_ITEMS : QUICK_ADD_ITEMS.filter((item) => item.id !== 'new-inspection')}
      showNotifications
      onOpenCustomize={() => setSettingsOpen(true)}
      showGlobalSearch={true}
      globalSearchApiClient={searchApiClient}
      showCommandPalette
      commands={canCreateQaJob ? COMMANDS : COMMANDS.filter((item) => item.id !== 'new-inspection')}
      hubLinks={HUB_LINKS}
      showOrgSwitcher
      sidebarSectionLabels={sidebarSectionLabels}
      headerRightActionsSlot={<OfflineStatusCompact />}
      showSettingsModal
      settingsTabs={settingsTabs}
      settingsModalOpen={settingsOpen}
      onCloseSettings={() => setSettingsOpen(false)}
      onOpenSettings={() => setSettingsOpen(true)}
      headerTabsSlot={<TabBar variant="inline" tabs={tabNav.tabItems} activeTabId={tabNav.activeTabId} onTabClick={tabNav.handleTabClick} onTabClose={tabNav.handleTabClose} onNewTab={tabNav.handleNewTab} onTabPin={tabNav.handleTabPin} onCloseOthers={tabNav.handleCloseOthers} onDuplicateTab={tabNav.handleDuplicateTab} accentColor="bg-blue-500" />}
    >
      {/* 
        Universal page container (Jobs Hub parity)
        WHY: Prevent content being clipped/overlapped by the sidebar collapse toggle,
        and standardize spacing across QA pages.
      */}
      <div className={outletClassName}>
        <Outlet />
      </div>
    </DashboardLayout>
  )
}

/**
 * Main App with SSO and routing
 * 
 * UNIFIED SESSION MANAGEMENT:
 * - Starts SessionManager on mount (monitors token expiration, activity)
 * - Syncs integration status on login (Xero, Simpro status in shared cookie)
 * - On session expiration → redirects to master login with returnTo
 * - On logout from any hub → all hubs detect and redirect
 */
function App() {
  // =========================================================================
  // SESSION MONITORING (unified across all hubs)
  // =========================================================================
  useEffect(() => {
    // Start session monitoring
    const manager = startSessionMonitoring({
      onSessionExpired: (reason) => {
        // SessionManager automatically redirects to master login
      },
      onTokenRefreshed: (newToken) => {
        
        // CRITICAL: Update Zustand store with the new token from SessionManager
        // SessionManager updates cookies directly, but API client reads from Zustand.
        // Without this sync, API requests would use stale tokens from Zustand.
        const refreshToken = getRefreshCookie()
        useAuthStore.getState().updateTokens(newToken, refreshToken || undefined)
        
        // Sync integration status with the new token
        syncIntegrationStatus(newToken).catch(err => {
          if (import.meta.env.DEV) console.warn('[QA Hub] Failed to sync integration status:', err)
        })
      },
    })

    // Sync integration status on mount
    syncIntegrationStatus().catch(err => {
      if (import.meta.env.DEV) console.warn('[QA Hub] Failed to sync integration status:', err)
    })

    return () => {
      stopSessionMonitoring()
    }
  }, [])

  return (
    <ErrorBoundary>
      {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
      <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
      {/* SSO Handler - processes token from URL (legacy support) */}
      <SSOHandler
        defaultRedirect="/records"  // QA Hub landing page
        onError={(error) => { if (import.meta.env.DEV) console.error('SSO Error:', error) }}
      />

      <Routes>
        {/* Unified SSO Route - standardized across all hubs */}
        <Route
          path="/sso"
          element={
            <UnifiedSSOPage 
              defaultRedirectPath="/records"
              loadingComponent={
                <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to QA Hub...</p>
                  </div>
                </div>
              }
            />
          }
        />

        {/* External Portal - No auth required */}
        <Route path="/portal/:token" element={<PortalPage />} />

        {/* Protected routes with shared layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute useMasterLogin>
              <HubGuard hub="qa">
                <QALayout />
              </HubGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/records" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="records" element={<RecordsPage />} />
          <Route path="records/new" element={<NewInspectionPage />} />
          <Route path="records/job/:jobNumber" element={<JobQaJobPage />} />
          <Route path="records/:id" element={<RecordDetailPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="templates/new" element={<TemplateDetailPage />} />
          <Route path="templates/:id" element={<TemplateDetailPage />} />
          <Route path="ncrs" element={<NCRsPage />} />
          <Route path="ncrs/new" element={<NCRDetailPage />} />
          <Route path="ncrs/:id" element={<NCRDetailPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/draft/:id" element={<ReportEditorPage />} />
          <Route path="evidence" element={<EvidencePage />} />
          <Route path="evidence/upload" element={<EvidenceUploadPage />} />
          <Route path="signoffs" element={<SignoffsPage />} />
          <Route path="areas/:areaId/execute" element={<AreaExecutionPage />} />
          <Route path="ai" element={<AIInsightsPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="chat" element={<ChatPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/records" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default App
